import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@/lib/api/client';
import { markTaskRead } from '@/services/taskService';
import type { BoardColumnPage } from '@/types/api';

/**
 * Tells the server a card has been opened, exactly once per opening.
 *
 * Deliberately a mutation rather than a query. As a query it re-ran on every cache
 * invalidation — and since marking read updates the row, which publishes a Realtime
 * event, which invalidates the cache, it re-ran forever.
 *
 * Nothing is refetched afterwards. The only thing that changed is a dot we already
 * know the new value of, so the cache is patched in place: clearing it costs one
 * request, not three.
 */
export function useMarkRead(taskId: string | null) {
  const queryClient = useQueryClient();

  const mark = useMutation<void, ApiError, string>({
    mutationFn: markTaskRead,
    onSuccess: (_result, id) => {
      queryClient.setQueriesData<{ pages: BoardColumnPage[] }>(
        { queryKey: ['boardColumn'] },
        (data) => (data ? { ...data, pages: data.pages.map((page) => clearUnread(page, id)) } : data),
      );
    },
  });

  const { mutate } = mark;
  useEffect(() => {
    // Keyed on the id alone: opening a different card marks that one, and re-renders
    // of the same open card do nothing.
    if (taskId) mutate(taskId);
  }, [taskId, mutate]);
}

/**
 * The column's unread count is a tally of its own cards, so it has to come down with
 * the card's dot. Returned as new objects rather than mutated — the sheet renders
 * from this and would not re-render otherwise.
 */
function clearUnread(page: BoardColumnPage, taskId: string): BoardColumnPage {
  const task = page.items.find((t) => t.id === taskId);
  if (!task || (!task.unread.isNew && !task.unread.hasActivity)) return page;

  return {
    ...page,
    unread: Math.max(0, page.unread - 1),
    items: page.items.map((t) =>
      t.id === taskId ? { ...t, unread: { isNew: false, hasActivity: false, count: 0 } } : t,
    ),
  };
}
